import type { ReactNode } from "react";
import type { TripPlannerData } from "@/lib/publicGuide";
import {
  AddRowButton,
  LabeledInput,
  LabeledTextarea,
  RowCard,
} from "./EditorKit";

type Preset = TripPlannerData["presets"][number];
type PresetDay = Preset["days"][number];
type Stop = PresetDay["stops"][number];

function Panel({ title, onRemove, children }: { title: string; onRemove: () => void; children: ReactNode }) {
  return (
    <div className="border border-background-200 rounded-md p-4 bg-background-50">
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-sm font-semibold text-foreground-900">{title}</p>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove"
          className="w-8 h-8 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 cursor-pointer shrink-0"
        >
          <i className="ri-delete-bin-line"></i>
        </button>
      </div>
      {children}
    </div>
  );
}

export default function PresetCard({
  preset,
  onChange,
  onRemove,
}: {
  preset: Preset;
  onChange: (preset: Preset) => void;
  onRemove: () => void;
}) {
  const updateDay = (index: number, day: PresetDay) =>
    onChange({ ...preset, days: preset.days.map((d, i) => (i === index ? day : d)) });

  return (
    <div className="border border-background-300 rounded-lg p-4 md:p-5 bg-background-100/50">
      <div className="flex items-start justify-between gap-3 mb-4">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-accent-700">
          Itinerary Preset
        </span>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove preset"
          className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 cursor-pointer shrink-0"
        >
          <i className="ri-delete-bin-line"></i>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <LabeledInput label="ID" value={preset.id} onChange={(value) => onChange({ ...preset, id: value })} />
        <LabeledInput label="Title" value={preset.title} onChange={(value) => onChange({ ...preset, title: value })} />
        <LabeledInput label="Duration" value={preset.duration} onChange={(value) => onChange({ ...preset, duration: value })} placeholder="2 days" />
        <LabeledInput label="Audience" value={preset.audience} onChange={(value) => onChange({ ...preset, audience: value })} />
      </div>

      <div className="mt-3 space-y-3">
        <LabeledTextarea
          label="Summary"
          value={preset.summary}
          onChange={(value) => onChange({ ...preset, summary: value })}
          rows={2}
        />
        <LabeledInput label="Cover image URL" value={preset.image} onChange={(value) => onChange({ ...preset, image: value })} />
      </div>

      <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mt-5 mb-2">
        Days
      </p>
      <div className="space-y-3">
        {preset.days.map((day, dayIndex) => (
          <Panel
            key={dayIndex}
            title={day.label || `Day ${dayIndex + 1}`}
            onRemove={() => onChange({ ...preset, days: preset.days.filter((_, i) => i !== dayIndex) })}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <LabeledInput label="Day label" value={day.label} onChange={(value) => updateDay(dayIndex, { ...day, label: value })} placeholder="Day 1" />
              <LabeledInput label="Theme" value={day.theme} onChange={(value) => updateDay(dayIndex, { ...day, theme: value })} />
            </div>

            <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mt-4 mb-2">
              Stops
            </p>
            <div className="space-y-3">
              {day.stops.map((stop, stopIndex) => (
                <RowCard
                  key={stopIndex}
                  title={stop.title || `Stop ${stopIndex + 1}`}
                  onRemove={() =>
                    updateDay(dayIndex, { ...day, stops: day.stops.filter((_, i) => i !== stopIndex) })
                  }
                >
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                    <LabeledInput label="Time" value={stop.time} onChange={(value) => updateStop(day, dayIndex, stopIndex, { ...stop, time: value })} placeholder="9:00 AM" />
                    <LabeledInput label="Title" value={stop.title} onChange={(value) => updateStop(day, dayIndex, stopIndex, { ...stop, title: value })} />
                    <LabeledInput label="Icon" value={stop.icon} onChange={(value) => updateStop(day, dayIndex, stopIndex, { ...stop, icon: value })} placeholder="ri-plant-line" />
                    <LabeledInput label="Link" value={stop.href || ""} onChange={(value) => updateStop(day, dayIndex, stopIndex, { ...stop, href: value })} placeholder="/experiences/…" />
                  </div>
                  <LabeledTextarea
                    label="Description"
                    value={stop.text}
                    onChange={(value) => updateStop(day, dayIndex, stopIndex, { ...stop, text: value })}
                    rows={2}
                  />
                </RowCard>
              ))}
            </div>
            <AddRowButton
              onClick={() =>
                updateDay(dayIndex, {
                  ...day,
                  stops: [...day.stops, { time: "", title: "", text: "", icon: "ri-map-pin-line", href: "" } as Stop],
                })
              }
              label="Add stop"
            />
          </Panel>
        ))}
      </div>
      <AddRowButton
        onClick={() => onChange({ ...preset, days: [...preset.days, { label: "", theme: "", stops: [] } as PresetDay] })}
        label="Add day"
      />
    </div>
  );

  function updateStop(day: PresetDay, dayIndex: number, stopIndex: number, stop: Stop) {
    const nextDay: PresetDay = { ...day, stops: day.stops.map((s, i) => (i === stopIndex ? stop : s)) };
    updateDay(dayIndex, nextDay);
  }
}